import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit as NotepadIcon, FaPlus, FaTimes, FaEdit, FaEye, FaCode, FaBold, FaItalic, FaStrikethrough, FaListUl, FaListOl, FaLink, FaQuoteLeft } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function NotepadComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { tabs: [], active_tab: 0 };
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editTabName, setEditTabName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localContent, setLocalContent] = useState(data.tabs[data.active_tab]?.content || '');
  const [cursorPos, setCursorPos] = useState(0);
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const textareaRef = useRef(null);
  const debounceTimer = useRef(null);

  // Sync local content with active tab (only when not typing)
  useEffect(() => {
    if (!isTyping) {
      setLocalContent(data.tabs[data.active_tab]?.content || '');
    }
  }, [data.active_tab, data.tabs, isTyping]);

  // Restore cursor position after update
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.selectionStart = cursorPos;
      textareaRef.current.selectionEnd = cursorPos;
    }
  }, [localContent, cursorPos]);

  const addTab = () => {
    const newTabs = [
      ...data.tabs,
      {
        name: `Tab ${data.tabs.length + 1}`,
        content: "",
        created_at: new Date().toISOString()
      }
    ];
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: newTabs.length - 1
      }
    });
  };

  const updateTabContent = (content, cursorPosition) => {
    setLocalContent(content);
    setCursorPos(cursorPosition);
    setIsTyping(true);

    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new debounce
    debounceTimer.current = setTimeout(() => {
      const updatedTabs = [...data.tabs];
      updatedTabs[data.active_tab].content = content;
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setIsTyping(false);
    }, 1000); // 1 second debounce
  };

  const deleteTab = (index) => {
    const newTabs = data.tabs.filter((_, i) => i !== index);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: Math.min(data.active_tab, newTabs.length - 1)
      }
    });
    setDeleteConfirm(null);
  };

  const startEditingTab = (index) => {
    setEditingTabIndex(index);
    setEditTabName(data.tabs[index].name);
  };

  const saveTabName = () => {
    if (editTabName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[editingTabIndex].name = editTabName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingTabIndex(null);
    }
  };

  const handleTextChange = (e) => {
    const { value, selectionStart } = e.target;
    updateTabContent(value, selectionStart);
  };

  // Markdown formatting functions
  const insertMarkdown = (before, after = '') => {
    if (!textareaRef.current) return;
    
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const selectedText = localContent.substring(start, end);
    const newText = localContent.substring(0, start) + before + selectedText + after + localContent.substring(end);
    const newCursorPos = start + before.length + selectedText.length + after.length;
    
    updateTabContent(newText, newCursorPos);
    
    // Restore focus
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const insertHeading = (level) => {
    const prefix = '#'.repeat(level) + ' ';
    insertMarkdown(prefix);
  };

  const insertList = (type) => {
    const prefix = type === 'ul' ? '- ' : '1. ';
    insertMarkdown(prefix);
  };

  const insertLink = () => {
    insertMarkdown('[', '](url)');
  };

  const insertCodeBlock = () => {
    insertMarkdown('```\n', '\n```');
  };

  // Improved markdown parser that protects code blocks
  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // Store code blocks temporarily
    const codeBlocks = [];
    let textWithPlaceholders = text.replace(/```([\s\S]*?)```/g, (match) => {
      const placeholder = `__CODEBLOCK_${codeBlocks.length}__`;
      codeBlocks.push(match);
      return placeholder;
    });
    
    // Store inline code temporarily
    const inlineCodes = [];
    textWithPlaceholders = textWithPlaceholders.replace(/`([^`]+)`/g, (match) => {
      const placeholder = `__INLINECODE_${inlineCodes.length}__`;
      inlineCodes.push(match);
      return placeholder;
    });
    
    let result = textWithPlaceholders
      // Escape HTML first
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers (must handle line start properly)
      .replace(/^### (.*)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2 text-[var(--text-primary)]">$1</h3>')
      .replace(/^## (.*)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2 text-[var(--text-primary)]">$1</h2>')
      .replace(/^# (.*)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-3 text-[var(--text-primary)]">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/__(.*?)__/g, '<strong class="font-semibold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/_(.*?)_/g, '<em class="italic">$1</em>')
      // Strikethrough
      .replace(/~~(.*?)~~/g, '<del class="line-through">$1</del>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[var(--primary)] underline hover:opacity-80">$1</a>')
      // Lists
      .replace(/^- (.*)$/gm, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.*)$/gm, '<li class="ml-4">$1</li>')
      // Blockquote
      .replace(/^> (.*)$/gm, '<blockquote class="border-l-4 border-[var(--primary)] pl-4 italic my-2 text-[var(--text-secondary)]">$1</blockquote>')
      // Line breaks and paragraphs
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      // Wrap in paragraph if not already wrapped
      .replace(/^(?!<[h1-6|pre|div|blockquote|li])/, '<p class="mb-2">')
      .replace(/(?<!>)$/, '</p>');
    
    // Restore inline codes
    inlineCodes.forEach((code, i) => {
      const content = code.slice(1, -1); // Remove backticks
      result = result.replace(`__INLINECODE_${i}__`, `<code class="bg-[var(--bg-secondary)] px-1 py-0.5 rounded text-sm font-mono">${content}</code>`);
    });
    
    // Restore code blocks
    codeBlocks.forEach((block, i) => {
      const content = block.slice(3, -3); // Remove ```
      result = result.replace(`__CODEBLOCK_${i}__`, `<pre class="bg-[var(--bg-secondary)] p-3 rounded mt-2 mb-2 overflow-x-auto border border-[var(--border-color)]"><code>${content}</code></pre>`);
    });
    
    return result;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="tab-bar flex items-center gap-1 mb-4">
        <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
          {data.tabs.map((tab, index) => (
            <div
              key={index}
              className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors text-sm ${
                index === data.active_tab 
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
              }`}
              onClick={() => onUpdate({
                ...component,
                data: {
                  ...data,
                  active_tab: index
                }
              })}
            >
              {editingTabIndex === index ? (
                <input
                  type="text"
                  className="px-1 bg-transparent rounded focus:outline-none w-20"
                  value={editTabName}
                  onChange={(e) => setEditTabName(e.target.value)}
                  onBlur={saveTabName}
                  onKeyPress={(e) => e.key === 'Enter' && saveTabName()}
                  autoFocus
                />
              ) : (
                <>
                  <span className={`truncate ${isFullscreen ? 'max-w-[200px]' : 'max-w-[120px]'}`} title={tab.name}>{tab.name}</span>
                  <button 
                    className="p-1 hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditingTab(index);
                    }}
                  >
                    <FaEdit size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                  </button>
                </>
              )}
              
              {data.tabs.length > 1 && (
                <button
                  className="p-1 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm({ index, tabName: tab.name });
                  }}
                >
                  <FaTimes size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                </button>
              )}
            </div>
          ))}
          
          <button
            className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm"
            onClick={addTab}
            title="Add new tab"
          >
            <FaPlus size={12} />
          </button>
        </div>

        {/* Markdown toggle button - always on the right */}
        {data.tabs.length > 0 && (
          <button
            className={`px-3 py-2 rounded-md transition-colors text-sm ml-2 flex items-center justify-center ${
              isMarkdownMode 
                ? 'bg-[var(--bg-hover)] text-[var(--text-primary)]' 
                : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
            }`}
            onClick={() => setIsMarkdownMode(!isMarkdownMode)}
            title={isMarkdownMode ? "Show raw markdown" : "Show styled preview"}
          >
            {isMarkdownMode ? <FaCode size={14} /> : <FaEye size={14} />}
          </button>
        )}
      </div>
      
      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto">
            <p className="mb-4">Are you sure you want to delete "{deleteConfirm.tabName}"?</p>
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)]"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
                onClick={() => deleteTab(deleteConfirm.index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Markdown Editor Toolbar */}
      {data.tabs.length > 0 && (
        <div className="mb-2 p-2 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)]">
          <div className="flex flex-wrap items-center gap-1">
            {/* Text formatting */}
            <button
              onClick={() => insertMarkdown('**', '**')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Bold"
            >
              <FaBold size={11} />
            </button>
            <button
              onClick={() => insertMarkdown('*', '*')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Italic"
            >
              <FaItalic size={11} />
            </button>
            <button
              onClick={() => insertMarkdown('~~', '~~')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Strikethrough"
            >
              <FaStrikethrough size={11} />
            </button>
            
            <div className="w-px h-5 bg-[var(--border-color)]"></div>
            
            {/* Headings */}
            <button
              onClick={() => insertHeading(1)}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs font-bold"
              title="Heading 1"
            >
              H1
            </button>
            <button
              onClick={() => insertHeading(2)}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs font-bold"
              title="Heading 2"
            >
              H2
            </button>
            <button
              onClick={() => insertHeading(3)}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs font-bold"
              title="Heading 3"
            >
              H3
            </button>
            
            <div className="w-px h-5 bg-[var(--border-color)]"></div>
            
            {/* Lists and blocks */}
            <button
              onClick={() => insertList('ul')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Bullet List"
            >
              <FaListUl size={11} />
            </button>
            <button
              onClick={() => insertList('ol')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Numbered List"
            >
              <FaListOl size={11} />
            </button>
            <button
              onClick={() => insertMarkdown('> ')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Quote"
            >
              <FaQuoteLeft size={11} />
            </button>
            <button
              onClick={insertCodeBlock}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Code Block"
            >
              <FaCode size={11} />
            </button>
            
            <div className="w-px h-5 bg-[var(--border-color)]"></div>
            
            {/* Link */}
            <button
              onClick={insertLink}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors flex items-center"
              title="Link"
            >
              <FaLink size={11} />
            </button>
            <button
              onClick={() => insertMarkdown('`', '`')}
              className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs font-mono"
              title="Inline Code"
            >
              {'<>'}
            </button>
          </div>
        </div>
      )}
      
      {isMarkdownMode ? (
        <div className="relative flex-1 min-h-0 rounded-lg overflow-hidden">
          {/* Styled markdown preview layer */}
          <div 
            className="absolute inset-0 p-4 overflow-y-auto pointer-events-none bg-[var(--bg-primary)] text-[var(--text-primary)]"
            style={{ 
              fontFamily: 'inherit',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}
          >
            {localContent ? (
              <div 
                className="max-w-none text-[var(--text-primary)]"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(localContent) }}
                style={{
                  color: 'var(--text-primary)',
                }}
              />
            ) : (
              <div className="text-[var(--text-secondary)] italic">
                {data.tabs.length ? "Start writing to see styled markdown..." : "Click the + button to create your first tab"}
              </div>
            )}
          </div>
          {/* Invisible textarea for editing */}
          <textarea
            ref={textareaRef}
            className="absolute inset-0 w-full h-full p-4 bg-transparent resize-none focus:outline-none focus:ring-1 focus:ring-[var(--primary)] rounded-lg"
            style={{
              color: 'transparent',
              caretColor: 'var(--text-primary)',
              lineHeight: '1.6'
            }}
            value={localContent}
            onChange={handleTextChange}
            placeholder={data.tabs.length ? "Start writing..." : "Click the + button to create your first tab"}
          />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className={`w-full p-4 rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--primary)] bg-[var(--bg-primary)] text-[var(--text-primary)] flex-1 min-h-0`}
          value={localContent}
          onChange={handleTextChange}
          placeholder={data.tabs.length ? "Start writing..." : "Click the + button to create your first tab"}
        />
      )}
    </div>
  );
}